import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, BookOpen, Mail, Check, X, CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";
import { adminListFeatures, adminDismissFeature } from "@/lib/api";

function StatusChip({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs rounded-full border px-2 py-0.5 ${
        ok ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-red-700 border-red-300 bg-red-50"
      }`}
      data-testid={`feature-status-${label.toLowerCase()}`}
    >
      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </span>
  );
}

export default function FeaturesPanel() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      setFeatures(await adminListFeatures());
    } catch {
      /* still */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dismiss = async (key) => {
    setBusy(key);
    try {
      await adminDismissFeature(key);
      setFeatures((prev) => prev.map((f) => (f.key === key ? { ...f, is_new: false } : f)));
      toast.success("Als erledigt markiert.");
    } catch {
      toast.error("Konnte nicht gespeichert werden.");
    } finally {
      setBusy(null);
    }
  };

  if (!features.length) return null;
  const newCount = features.filter((f) => f.is_new).length;

  return (
    <div className="space-y-3" data-testid="admin-features">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Neuerungen / Optimieren</h2>
        {newCount > 0 && (
          <span
            className="text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5"
            data-testid="features-new-count"
          >
            {newCount} neu
          </span>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {features.map((f) => (
          <div
            key={f.key}
            className="bg-white border border-border rounded-lg p-4 space-y-3"
            data-testid={`feature-card-${f.key}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{f.title}</h3>
                  {f.is_new && (
                    <span
                      className="text-[10px] uppercase tracking-wide bg-primary text-primary-foreground rounded-full px-2 py-0.5"
                      data-testid={`feature-new-badge-${f.key}`}
                    >
                      Neu
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{f.description}</p>
              </div>
              {f.added && <span className="text-xs text-muted-foreground shrink-0">{f.added}</span>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatusChip ok={f.handbook_ok} label="Handbuch" />
              <StatusChip ok={f.automail_ok} label="AutoMail" />
            </div>

            {Array.isArray(f.history) && f.history.length > 0 && (
              <div className="border-t border-border pt-2" data-testid={`feature-history-${f.key}`}>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
                  <History className="h-3.5 w-3.5" /> Änderungsverlauf
                </div>
                <ul className="space-y-1">
                  {[...f.history]
                    .sort((a, b) => (b.version || 0) - (a.version || 0))
                    .map((h) => (
                      <li key={h.version} className="text-xs text-muted-foreground flex gap-2">
                        <span className="shrink-0 font-medium text-foreground">v{h.version}</span>
                        <span className="shrink-0 tabular">{h.date}</span>
                        <span className="min-w-0">{h.note}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => navigate("/handbuch")}
                className="inline-flex items-center gap-1.5 text-xs border border-border rounded-md px-2.5 py-1 hover:bg-muted transition-colors"
                data-testid={`feature-edit-handbook-${f.key}`}
              >
                <BookOpen className="h-3.5 w-3.5" /> Handbuch öffnen
              </button>
              <button
                type="button"
                onClick={() => navigate("/mails")}
                className="inline-flex items-center gap-1.5 text-xs border border-border rounded-md px-2.5 py-1 hover:bg-muted transition-colors"
                data-testid={`feature-edit-automail-${f.key}`}
              >
                <Mail className="h-3.5 w-3.5" /> AutoMail öffnen
              </button>
              {f.is_new && (
                <button
                  type="button"
                  onClick={() => dismiss(f.key)}
                  disabled={busy === f.key}
                  className="inline-flex items-center gap-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-md px-2.5 py-1 hover:bg-emerald-50 transition-colors ml-auto"
                  data-testid={`feature-dismiss-${f.key}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Als erledigt markieren
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
